'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TbArrowLeft, TbArrowRight } from 'react-icons/tb';
import { LuMapPinned } from 'react-icons/lu';
import type { Locale } from '@/i18n';
import ScrollReveal from '@/app/components/animations/ScrollReveal';

interface Testimonial {
    clientName: string;
    clientRole: string;
    company: string;
    quote: string;
    avatar?: any;
    /** 1-5. Set on reviews copied from Google; absent on written testimonials. */
    rating?: number | null;
    source?: 'direct' | 'google' | string | null;
}

interface TestimonialsSectionProps {
    title?: string;
    testimonials: Testimonial[];
    locale: Locale;
    /** Google Business Profile link. Omit to hide the review invitation. */
    reviewUrl?: string | null;
}

const AUTOPLAY_MS = 4500;
const FOLD = 28;
const INK = '#081821';
const RED = '#ea3228';
const PURPLE = '#7a2ff2';
// Alternated by realIndex instead of always red, so which testimonial is
// "active" doesn't repeat the same red highlight every single time.
const ACTIVE_ACCENTS = [RED, PURPLE];

// Desktop/mobile geometry for the fanned "deck" — how far each card sits from
// the active slot, and how much it rotates, scaled by |offset| from center.
// Wide steps so the deck spans the full section width, hover.dev-style.
// Heights are fixed (not responsive via Tailwind) because the SVG viewBox
// below needs to match the rendered box size exactly, in real pixels.
const GEOMETRY = {
    mobile: { cardWidth: 240, step: 165, rotateStep: 5, height: 190, activeHeight: 230 },
    desktop: { cardWidth: 340, step: 260, rotateStep: 6, height: 200, activeHeight: 250 },
};

function slotTransform(offset: number, dir: number, isMobile: boolean) {
    const g = isMobile ? GEOMETRY.mobile : GEOMETRY.desktop;
    const abs = Math.abs(offset);
    return {
        x: offset * dir * g.step,
        rotate: offset === 0 ? 0 : -offset * dir * g.rotateStep,
        scale: offset === 0 ? 1 : Math.max(0.86, 1 - abs * 0.07),
    };
}

function edgeTransform(offset: number, dir: number, isMobile: boolean) {
    const sign = offset === 0 ? 0 : offset > 0 ? 1 : -1;
    return slotTransform(offset + sign, dir, isMobile);
}

function initials(name: string) {
    return name.trim().charAt(0).toUpperCase();
}

interface CardProps {
    testimonial: Testimonial;
    offset: number;
    dir: number;
    isMobile: boolean;
    isAr: boolean;
    realIndex: number;
    onSelect: () => void;
}

function TestimonialCard({ testimonial, offset, dir, isMobile, isAr, realIndex, onSelect }: CardProps) {
    const active = offset === 0;
    const accent = ACTIVE_ACCENTS[realIndex % ACTIVE_ACCENTS.length];
    const g = isMobile ? GEOMETRY.mobile : GEOMETRY.desktop;
    const target = slotTransform(offset, dir, isMobile);
    const edge = edgeTransform(offset, dir, isMobile);
    const w = g.cardWidth;
    const h = active ? g.activeHeight : g.height;
    const fold = Math.min(FOLD, w * 0.25, h * 0.25);

    return (
        <motion.button
            type="button"
            onClick={active ? undefined : onSelect}
            aria-hidden={active ? undefined : true}
            tabIndex={active ? -1 : 0}
            className="absolute left-1/2 top-1/2 text-left rtl:text-right"
            style={{ zIndex: 50 - Math.abs(offset) * 10, width: w, marginLeft: -w / 2 }}
            initial={{ x: edge.x, y: '-50%', rotate: edge.rotate, scale: edge.scale, opacity: 0 }}
            animate={{ x: target.x, y: '-50%', rotate: target.rotate, scale: target.scale, opacity: 1 }}
            exit={{ x: edge.x, y: '-50%', rotate: edge.rotate, scale: edge.scale, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
        >
            <div className="relative" style={{ width: w, height: h, filter: 'drop-shadow(0 20px 40px rgba(3,10,25,0.35))' }}>
                {/* The card shape is an SVG polygon (not a CSS clip-path) so the
                    stroke traces every edge, including the diagonal fold cut —
                    a plain `border` never paints along a clip-path's own edge. */}
                <svg
                    className="absolute inset-0 h-full w-full"
                    viewBox={`0 0 ${w} ${h}`}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                >
                    <polygon
                        points={`0,0 ${w - fold},0 ${w},${fold} ${w},${h} 0,${h}`}
                        fill={active ? accent : '#ffffff'}
                        stroke={INK}
                        strokeWidth={3}
                        strokeLinejoin="round"
                    />
                    
                </svg>

                <div className={`relative flex h-full flex-col justify-between p-6 ${active ? 'text-white' : 'text-[color:var(--brand-ink)]'}`}>
                    <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-heading text-lg font-black ${
                            active ? 'bg-white/20 text-white' : ''
                        }`}
                        style={active ? undefined : { backgroundColor: `${accent}1f`, color: accent }}
                    >
                        {initials(testimonial.clientName)}
                    </div>

                    {testimonial.rating ? (
                        <p
                            className={`mt-3 text-sm tracking-[0.18em] ${active ? 'text-brand-text' : 'text-[color:var(--brand-ink)]'}`}
                            aria-label={isAr ? `${testimonial.rating} من 5` : `${testimonial.rating} out of 5`}
                        >
                            <span aria-hidden="true">
                                {'★'.repeat(Math.round(testimonial.rating))}
                                {'☆'.repeat(Math.max(0, 5 - Math.round(testimonial.rating)))}
                            </span>
                        </p>
                    ) : null}

                    <p className={`mt-4 line-clamp-4 text-sm font-body leading-relaxed sm:text-base ${active ? 'text-white' : 'text-[color:var(--brand-ink)]'}`}>
                        &quot;{testimonial.quote}&quot;
                    </p>

                    {/* A Google review has no job title, so the "role at company"
                        template would render "— Name,  at ". Falls back to name,
                        plus a Google label so a public review is never presented
                        as a testimonial given to us privately. */}
                    <p className={`mt-4 text-xs italic ${active ? 'text-white/75' : 'text-[color:var(--brand-ink)]/60'}`}>
                        {testimonial.clientRole
                            ? (isAr
                                ? `— ${testimonial.clientName}، ${testimonial.clientRole} في ${testimonial.company}`
                                : `— ${testimonial.clientName}, ${testimonial.clientRole} at ${testimonial.company}`)
                            : `— ${testimonial.clientName}${
                                testimonial.source === 'google'
                                    ? (isAr ? ' · تقييم على جوجل' : ' · Review on Google')
                                    : testimonial.company ? ` · ${testimonial.company}` : ''
                            }`}
                    </p>
                </div>
            </div>
        </motion.button>
    );
}

export default function TestimonialsSection({ title, testimonials, locale, reviewUrl }: TestimonialsSectionProps) {
    const isAr = locale === 'ar';
    const safeTestimonials = testimonials?.filter((testimonial) => testimonial.clientName && testimonial.quote) || [];
    const count = safeTestimonials.length;

    const [active, setActive] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [paused, setPaused] = useState(false);
    const touchStartX = useRef<number | null>(null);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    useEffect(() => {
        if (count < 2 || paused) return;
        const id = setInterval(() => setActive((i) => (i + 1) % count), AUTOPLAY_MS);
        return () => clearInterval(id);
    }, [count, paused]);

    const goTo = useCallback(
        (delta: 1 | -1) => setActive((i) => (i + delta + count) % count),
        [count],
    );

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 40) goTo(delta < 0 ? 1 : -1);
        touchStartX.current = null;
    };

    // Previously returned null with no testimonials, so removing the fabricated
    // ones would have deleted the section outright. With a review link there is
    // still something worth showing — and an empty wall of praise is a weaker
    // signal than an honest invitation to be the first.
    if (count === 0 && !reviewUrl) return null;

    const maxSide = count >= 6 ? 2 : count >= 3 ? 1 : 0;
    const dir = isAr ? -1 : 1;
    const slots: { testimonial: Testimonial; offset: number; realIndex: number }[] = [];
    // Guarded on count: with none, the modulo below is NaN and the loop would
    // still push one slot holding `undefined`, crashing the card.
    if (count > 0) {
        for (let offset = -maxSide; offset <= maxSide; offset++) {
            const realIndex = ((active + offset) % count + count) % count;
            slots.push({ testimonial: safeTestimonials[realIndex], offset, realIndex });
        }
    }

    return (
        <section
            className="perf-deferred relative overflow-hidden bg-brand-bg py-32 text-brand-text"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <ScrollReveal mode="fade-up" className="mb-16 px-4 text-center md:mb-20 md:px-10">
                <h2 className="mb-6 font-heading text-4xl font-black md:text-6xl">
                    {title || (isAr ? 'قصص نجاح عملائنا' : 'Client Success Stories')}
                </h2>
                <div className="mx-auto h-1 w-24 bg-brand-purple" />
            </ScrollReveal>

            <div
                className={`relative w-full ${count > 0 ? 'h-[300px] sm:h-[320px] md:h-[360px]' : 'h-0'}`}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <AnimatePresence initial={false}>
                    {slots.map(({ testimonial, offset, realIndex }) => (
                        <TestimonialCard
                            key={realIndex}
                            testimonial={testimonial}
                            offset={offset}
                            dir={dir}
                            isMobile={isMobile}
                            isAr={isAr}
                            realIndex={realIndex}
                            onSelect={() => setActive(realIndex)}
                        />
                    ))}
                </AnimatePresence>
            </div>

            {count > 1 && (
                <div className="mt-10 flex items-center justify-center gap-4">
                    <button
                        type="button"
                        onClick={() => goTo(-1)}
                        aria-label={isAr ? 'الشهادة السابقة' : 'Previous testimonial'}
                        className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[color:var(--brand-ink)] bg-white text-[color:var(--brand-ink)] transition-colors duration-200 hover:bg-brand-text"
                    >
                        <TbArrowLeft className="h-5 w-5 rtl:-scale-x-100" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        onClick={() => goTo(1)}
                        aria-label={isAr ? 'الشهادة التالية' : 'Next testimonial'}
                        className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[color:var(--brand-ink)] bg-white text-[color:var(--brand-ink)] transition-colors duration-200 hover:bg-brand-text"
                    >
                        <TbArrowRight className="h-5 w-5 rtl:-scale-x-100" aria-hidden="true" />
                    </button>
                </div>
            )}

            {/* Shown whether or not testimonials exist. With none it carries the
                section on its own; with some, it is the ask that turns a reader
                into the next one. Points at the Google Business Profile, so a
                review lands somewhere independently verifiable rather than in a
                form only we can see. */}
            {reviewUrl && (
                <ScrollReveal mode="fade-up" className={count > 0 ? 'mt-16 px-4 md:px-10' : 'px-4 md:px-10'}>
                    <div className="mx-auto max-w-2xl rounded-3xl border border-brand-text/15 bg-white/[0.04] p-8 text-center backdrop-blur-sm md:p-10">
                        <p className="font-heading text-2xl font-black text-brand-text md:text-3xl">
                            {count > 0
                                ? (isAr ? 'عملت معنا؟' : 'Worked with us?')
                                : (isAr ? 'كن أول من يشاركنا رأيه' : 'Be the first to share your experience')}
                        </p>
                        <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-white/75 md:text-lg">
                            {isAr
                                ? 'تقييمك على خرائط جوجل يساعد علامات أخرى في الرياض على معرفة ما نقدّمه فعلًا — ويستغرق دقيقة واحدة.'
                                : 'A review on Google Maps helps other brands in Riyadh see what we actually deliver — and it takes about a minute.'}
                        </p>
                        <a
                            href={reviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-7 inline-flex min-h-12 items-center gap-2 rounded-full bg-brand-text px-7 py-3 font-heading text-base font-black text-[#081821] transition-transform duration-200 hover:-translate-y-0.5"
                        >
                            <LuMapPinned className="h-5 w-5" aria-hidden="true" />
                            {isAr ? 'اكتب تقييمك على جوجل' : 'Write a review on Google'}
                        </a>
                    </div>
                </ScrollReveal>
            )}
        </section>
    );
}
